import { Terminal, Copy, Check } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib/utils"
import { SITE_HOST } from "@/lib/config/site"

const INSTALL_CMD = `curl -fsSL ${SITE_HOST}/install.sh | sh`

const DEMO_LINES = [
  { prompt: true, text: "footage login" },
  { prompt: false, text: "Opening browser to authorize..." },
  { prompt: false, text: "Authorized!" },
  { prompt: false, text: "" },
  { prompt: true, text: 'footage "scan ~/Movies and make a highlight reel"' },
  { prompt: false, text: "[scanning] Found 47 clips across 3 folders..." },
  { prompt: false, text: "[footage] Selecting best moments, creating timeline..." },
  { prompt: false, text: "[generating] 12 clips queued" },
]

export function CLISection() {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(INSTALL_CMD)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <section className="relative px-4 py-20 md:py-28">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground mb-6">
            <Terminal className="size-3" />
            Command Line
          </div>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tighter text-balance mb-4">
            Your footage, from the terminal
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto text-balance">
            Scan local media, create projects, and direct AI video generation without leaving your terminal.
          </p>
        </div>

        {/* Install command */}
        <div className="flex items-center justify-center mb-10">
          <button
            onClick={handleCopy}
            className={cn(
              "group flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-5 py-3",
              "hover:bg-muted/50 transition-colors cursor-pointer"
            )}
          >
            <code className="text-sm font-mono text-foreground">
              {INSTALL_CMD}
            </code>
            {copied ? (
              <Check className="size-4 text-emerald-500 shrink-0" />
            ) : (
              <Copy className="size-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
            )}
          </button>
        </div>

        {/* Terminal demo */}
        <div className="rounded-xl border border-border bg-[#0d1117] overflow-hidden shadow-2xl">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
            <div className="flex gap-1.5">
              <div className="size-3 rounded-full bg-[#ff5f57]" />
              <div className="size-3 rounded-full bg-[#febc2e]" />
              <div className="size-3 rounded-full bg-[#28c840]" />
            </div>
            <span className="text-xs text-white/30 font-mono ml-2">footage</span>
          </div>
          {/* Terminal content */}
          <div className="p-5 font-mono text-sm leading-relaxed">
            {DEMO_LINES.map((line, i) => (
              <div key={i} className={cn(line.text === "" && "h-3")}>
                {line.prompt && (
                  <span className="text-emerald-400">$ </span>
                )}
                <span className={line.prompt ? "text-white" : "text-white/50"}>
                  {line.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Feature bullets */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          {[
            { title: "Scan local media", desc: "Point at a folder and let AI catalog your footage" },
            { title: "AI creative director", desc: "Natural language control over your video timeline" },
            { title: "Full project control", desc: "Create, export, and manage projects from CLI" },
          ].map((f) => (
            <div key={f.title} className="text-center">
              <p className="font-medium text-sm mb-1">{f.title}</p>
              <p className="text-xs text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
